import { useEffect, useRef } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { cn } from "@/lib/utils";
import { KioskClock } from "./KioskClock";
import { DURATION } from "./motion";

export interface KioskScreensaverProps {
  show: boolean;
  style: "clock" | "blank";
  onWake?: () => void;
  className?: string;
}

/**
 * Full-screen screensaver overlay for kiosk / wall-tablet mode.
 *
 * Fades in/out with AnimatePresence (DURATION.slow, reduced-motion safe).
 * Any pointerdown or keydown on the overlay calls onWake and is swallowed so
 * the first touch/keypress wakes rather than acting on the UI beneath.
 *
 * The overlay is made focusable (tabIndex={0}) so a physical key-press is
 * captured before it can reach underlying controls.
 */
export function KioskScreensaver({ show, style, onWake, className }: KioskScreensaverProps) {
  const reduced = useReducedMotion();
  const overlayRef = useRef<HTMLDivElement>(null);

  // Move focus onto the overlay when it appears so keyboard wakes work
  // immediately without the user having to tab to it first.
  useEffect(() => {
    if (show) {
      overlayRef.current?.focus();
    }
  }, [show]);

  function handleWake(e: React.PointerEvent | React.KeyboardEvent) {
    e.stopPropagation();
    e.preventDefault();
    onWake?.();
  }

  const transition = {
    duration: reduced ? 0 : DURATION.slow,
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          ref={overlayRef}
          key="kiosk-screensaver"
          data-slot="kiosk-screensaver"
          role="presentation"
          aria-hidden={!show}
          // Focusable so keyboard events are captured on the overlay
          tabIndex={0}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={transition}
          onPointerDown={handleWake}
          onKeyDown={handleWake}
          className={cn(
            "fixed inset-0 z-[60] focus:outline-none",
            // bg-black intentional: true #000 for OLED wall tablets
            style === "blank" && "bg-black",
            className,
          )}
        >
          {style === "clock" && <KioskClock />}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
